import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import { PropertyCard, useCompanyDateTime, MiniLoading, StylovyzeTooltip, palette, Text } from '@innovyze/stylovyze';
import { AddToPhotos, Delete, Help, Publish, Unpublished, CheckCircle, RemoveCircle } from '@mui/icons-material';
import { Link, Stack, Box, Tooltip } from '@mui/material';
import { formatDateTimeWithFormatDate } from '@innovyze/lib-am-common/Utils';
import { getTenantId } from '@innovyze/lib_get_service';

import { DeteriorationModelCard as DeteriorationModelCardType, GroupSet, GetFailureDefinitionResponse } from '@Types';
import { useGlobalization } from '@Translations';
import { addRouteNamespace } from '@Utils/actions';
import { getGroupSet } from '@Services/groupSets.services';
import { getFailureDefinition } from '@Services/failureDefinition.services';

interface DeteriorationModelCardProps {
	model: DeteriorationModelCardType;
	onDuplicate: (modelId: string) => Promise<void>;
	onDelete: (modelId: string) => void;
	onPublishToggle: (modelId: string, published: boolean) => void;
}

const DraftStatusTag = () => {
	return (
		<div
			style={{
				background: '#ffffff',
				border: '0.0625rem solid currentColor',
				boxSizing: 'content-box',
				borderRadius: '2px',
				padding: '0 1rem',
				textTransform: 'uppercase',
				fontSize: '0.625rem',
				color: '#4a6067',
				width: 'fit-content',
			}}>
			DRAFT
		</div>
	);
};

export const DeteriorationModelCard = ({
	model,
	onDuplicate,
	onDelete,
	onPublishToggle,
}: DeteriorationModelCardProps) => {
	const { t } = useGlobalization();
	const history = useHistory();
	const { formatDate } = useCompanyDateTime();
	const [isDuplicating, setIsDuplicating] = useState(false);

	const [groupSet, setGroupSet] = useState<{
		isLoading: boolean;
		error: string | null;
		groupSet: GroupSet | null;
	}>({
		isLoading: false,
		error: null,
		groupSet: null,
	});

	const [failureDefinition, setFailureDefinition] = useState<{
		isLoading: boolean;
		error: string | null;
		failureDefinition: GetFailureDefinitionResponse | null;
	}>({
		isLoading: false,
		error: null,
		failureDefinition: null,
	});

	useEffect(() => {
		const fetchGroupSet = async () => {
			if (!model.assetGroupSetId) return;

			setGroupSet(prev => ({
				...prev,
				isLoading: true,
				error: null,
			}));

			try {
				const response = await getGroupSet(model.assetGroupSetId);
				setGroupSet(prev => ({
					...prev,
					groupSet: response.data.data,
				}));
			} catch (error) {
				console.error('Error fetching group set:', error);
				setGroupSet(prev => ({
					...prev,
					error: 'Failed to load group set',
				}));
			} finally {
				setGroupSet(prev => ({
					...prev,
					isLoading: false,
				}));
			}
		};

		fetchGroupSet();
	}, [model.assetGroupSetId]);

	useEffect(() => {
		const fetchFailureDefinition = async () => {
			if (!model.failureDefinitionId) return;

			setFailureDefinition(prev => ({
				...prev,
				isLoading: true,
				error: null,
			}));

			try {
				const tenantId = getTenantId() || '';
				const response = await getFailureDefinition(model.failureDefinitionId, tenantId);
				setFailureDefinition(prev => ({
					...prev,
					failureDefinition: response.data,
				}));
			} catch (error) {
				console.error('Error fetching failure definition:', error);
				setFailureDefinition(prev => ({
					...prev,
					error: 'Failed to load failure definition',
				}));
			} finally {
				setFailureDefinition(prev => ({
					...prev,
					isLoading: false,
				}));
			}
		};

		fetchFailureDefinition();
	}, [model.failureDefinitionId]);

	const handleDuplicate = async () => {
		setIsDuplicating(true);
		try {
			await onDuplicate(model.id);
		} catch (error) {
			console.error('Error Duplicating deterioration model: ', error);
		} finally {
			setIsDuplicating(false);
		}
	};

	const handleDelete = () => {
		onDelete(model.id);
	};

	const handlePublishToggle = () => {
		onPublishToggle(model.id, !model.published);
	};

	return (
		<PropertyCard
			columns={2}
			forceMenu
			menuItems={[
				{
					text: t('Duplicate'),
					icon: <AddToPhotos />,
					onClick: handleDuplicate,
					props: {
						disabled: isDuplicating,
					},
				},
				{
					text: t('Delete'),
					icon: <Delete />,
					onClick: handleDelete,
					props: {
						disabled: model.published,
					},
				},
				{
					text: model.published ? t('Unpublish') : t('Publish'),
					icon: model.published ? <Unpublished /> : <Publish />,
					onClick: handlePublishToggle,
					props: {
						disabled: !model.published && !model.runAt,
					},
				},
			]}
			className="deterioration-model-card"
			properties={[
				{
					title: t('Last Run'),
					value: <Text>{`${model.runAt ? formatDateTimeWithFormatDate(model.runAt, formatDate) : '-'}`}</Text>,
				},
				model.status === 'draft'
					? {
							title: t('Status'),
							value: <DraftStatusTag />,
					  }
					: {
							title: t('Status'),
							type: model.status ? 'status' : 'text',
							value: model.status,
					  },
				{
					title: t('System and Asset Type'),
					value: groupSet.isLoading ? (
						<MiniLoading loading={true} />
					) : groupSet.error ? (
						<Text>{'-'}</Text>
					) : groupSet.groupSet?.systemType && groupSet.groupSet?.assetType ? (
						<Text>{`${groupSet.groupSet?.systemType} - ${groupSet.groupSet?.assetType}`}</Text>
					) : (
						<Text>{'-'}</Text>
					),
				},
				{
					title: t('Number of Asset Groups'),
					value: groupSet.isLoading ? (
						<MiniLoading loading={true} />
					) : groupSet.error ? (
						<Text>{'-'}</Text>
					) : (
						<Stack direction="row" spacing={1} alignItems="center">
							<Text>{`${groupSet.groupSet?.actions?.length || 0}`}</Text>
							<StylovyzeTooltip
								content={t(
									'Can differ from the number of groups in the asset group set because some may have been excluded from the model.',
								)}>
								<Help sx={{ fontSize: 'small', color: palette.secondary.main }} />
							</StylovyzeTooltip>
						</Stack>
					),
				},
				{
					title: t('Asset Group Name'),
					value: groupSet.isLoading ? (
						<MiniLoading loading={true} />
					) : groupSet.error ? (
						<Text>{'-'}</Text>
					) : (
						<Text>{groupSet.groupSet?.name || '-'}</Text>
					),
				},
				{
					title: t('Failure Definition'),
					value: failureDefinition.isLoading ? (
						<MiniLoading loading={true} />
					) : failureDefinition.error ? (
						<Text>{'-'}</Text>
					) : (
						<Text>{failureDefinition.failureDefinition?.failure_definition.name || '-'}</Text>
					),
				},
			]}
			title={
				<Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
					<Link
						underline="hover"
						style={{
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							cursor: 'pointer',
							flex: 1,
							whiteSpace: 'nowrap',
						}}
						title={model.name}
						onClick={() => history.push(addRouteNamespace(`models/${model.id}`))}>
						{model.name}
					</Link>
					{model.published ? (
						<Tooltip title={t('Published')} arrow>
							<CheckCircle sx={{ color: '#4caf50', fontSize: 20, ml: 1, flexShrink: 0 }} />
						</Tooltip>
					) : (
						<Tooltip title={t('Unpublished')} arrow>
							<RemoveCircle sx={{ color: '#9e9e9e', fontSize: 20, ml: 1, flexShrink: 0 }} />
						</Tooltip>
					)}
				</Box>
			}
		/>
	);
};
